import React, { type ReactNode, useState } from 'react';

import { Avatar, Popover } from 'antd';

import { sanitizeToText } from '@/shared/lib/sanitize';
import { Icon } from '@/shared/ui/Icon';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { useUserDetail } from '@/entities/user';

import styles from './UserPopover.module.css';

type UserPopoverProps = {
  userName: string;
  avatarColor: string;
  initials: string | ReactNode;
  avatarSize?: number;
  placement?: 'top' | 'topLeft' | 'topRight' | 'bottom' | 'bottomLeft' | 'bottomRight';
  zIndex?: number;
  className?: string;
};

export const UserPopover: React.FC<UserPopoverProps> = ({
  userName,
  avatarColor,
  initials,
  avatarSize = 24,
  placement = 'topLeft',
  zIndex,
  className,
}) => {
  const [hasOpened, setHasOpened] = useState(false);

  const { data: userDetail, isLoading } = useUserDetail({
    userName,
    enabled: hasOpened,
  });

  const handleOpenChange = (open: boolean) => {
    if (open && !hasOpened) {
      setHasOpened(true);
    }
  };

  const renderPopoverContent = () => {
    if (!hasOpened || isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <LoadingState message="" size={24} />
        </div>
      );
    }

    if (!userDetail) {
      return (
        <div className={styles.popoverContent}>
          <div className={styles.popoverRow}>
            <span className={styles.userName}>사용자 정보를 불러올 수 없습니다</span>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.popoverContent}>
        {userDetail.displayName && (
          <div className={styles.popoverRow}>
            <span className={styles.userName}>{userDetail.displayName}</span>
          </div>
        )}
        {userDetail.email && (
          <div className={styles.popoverRow}>
            <span className={styles.userEmail}>{userDetail.email}</span>
          </div>
        )}
        {userDetail.teams && userDetail.teams.length > 0 && (
          <>
            <div className={styles.popoverDivider} />
            {userDetail.teams.map((team) => {
              const teamDescription = team.description ? sanitizeToText(team.description).trim() : '';
              return (
                <div key={team.id} className={styles.popoverRow}>
                  <Icon name="users" size={14} className={styles.teamIcon} />
                  <span className={styles.teamName} title={teamDescription}>
                    {team.displayName || team.name}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  const avatarStyle = `${styles.avatar} ${styles[`avatarSize${avatarSize}`] || ''}`;
  const wrapperStyle = zIndex !== undefined ? ({ '--z-index': zIndex } as React.CSSProperties) : undefined;

  return (
    <Popover
      content={renderPopoverContent()}
      placement={placement}
      rootClassName={styles.userPopover}
      trigger="hover"
      onOpenChange={handleOpenChange}
      mouseEnterDelay={0.1}
      mouseLeaveDelay={0.1}>
      <div className={`${styles.avatarWrapper} ${className || ''}`} style={wrapperStyle}>
        <Avatar
          className={avatarStyle}
          size={avatarSize}
          style={{ backgroundColor: avatarColor, borderColor: avatarColor, fontSize: `${(avatarSize / 3) * 2}px` }}>
          {initials}
        </Avatar>
      </div>
    </Popover>
  );
};
